"use client";

import { Button, IconButton, Skeleton } from "@dafiti/closet-ui";
import { Like, LikeFilled } from "@dafiti/closet-ui/icons";
import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";

export type AddToWishlistButtonProps = {
  variant?: "text" | "icon";
  filled?: boolean;
  className?: string;
  isWishlisted: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  href?: string;
  "data-testid"?: string;
};

export const AddToWishlistButton = ({
  variant = "text",
  className,
  isWishlisted,
  onClick,
  filled,
  isLoading,
  href,
  "data-testid": dataTestId,
}: AddToWishlistButtonProps) => {
  const t = useTranslations("wishlist");

  if (isLoading) {
    return (
      <AddToWishlistButtonSkeleton variant={variant} className={className} />
    );
  }

  const ButtonElement = variant === "text" ? Button : IconButton;
  const Child = href ? "a" : "button";

  return (
    <ButtonElement
      className={cn(
        "w-full gap-2 group border-gray-600 hover:border-black overflow-hidden relative",
        {
          "size-[48px]": variant === "icon",
          "bg-white border-none": filled,
        },
        className,
      )}
      size="md"
      variant="secondary"
      onClick={onClick}
      aria-label={
        isWishlisted ? t("button.ariaLabel.remove") : t("button.ariaLabel.add")
      }
      asChild
      href={href}
      data-testid={dataTestId || "toggle-wishlist-button"}
    >
      <Child>
        <span
          className={cn(
            "transition-all duration-300 ease-in-out group-active:scale-90 overflow-visible",
            {
              "scale-110": isWishlisted,
              "scale-100": !isWishlisted,
            },
          )}
          style={{
            animation: isWishlisted
              ? "heartbeat 0.8s cubic-bezier(0.68, -0.6, 0.32, 1.6)"
              : "none",
          }}
        >
          {!isWishlisted ? (
            <Like
              className="!size-4 !fill-gray-600 group-hover:!fill-black duration-200 transition-colors"
              aria-hidden="true"
              data-testid="add-to-wishlist-icon"
            />
          ) : (
            <LikeFilled
              className="!size-4 fill-danger-200"
              aria-hidden="true"
              data-testid="added-to-wishlist-icon"
            />
          )}
        </span>

        {variant === "text" && (
          <span
            className="transition-[width] duration-300 block whitespace-nowrap text-left"
            style={{
              width: isWishlisted ? "160px" : "140px",
            }}
          >
            {isWishlisted ? t("button.added") : t("button.add")}
          </span>
        )}

        <style jsx>{`
          @keyframes heartbeat {
            0% {
              transform: scale(1);
            }
            40% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
            }
          }
        `}</style>
      </Child>
    </ButtonElement>
  );
};

export const AddToWishlistButtonSkeleton = (
  props: Pick<AddToWishlistButtonProps, "variant" | "className">,
) => {
  return (
    <Skeleton
      className={cn([
        "h-[48px] rounded-full",
        {
          "w-full": props.variant === "text",
          "w-[48px]": props.variant === "icon",
        },
        props.className,
      ])}
    />
  );
};
